import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { CButton } from "@coreui/react-pro";
import { DirectionIcon } from "@scryerlabs/ui";
import type { PortfolioAnswer, PortfolioNextDto } from "../../../api/hooks/usePortfolioBuilder";
import { CHECK_ID, REFINE_ID, SIDE_QUESTION_ID } from "../portfolio-builder";

interface QuestionCardProps {
  hidden: boolean;
  onHiddenChange: (hidden: boolean) => void;
  /** False until "Can your account sell short?" is answered, which the page asks before anything else. */
  sideAnswered: boolean;
  /** What the API says comes next. Null before the first match arrives. */
  next: PortfolioNextDto | null;
  /** The match for the latest answers failed, so there is nothing to ask until one is undone. */
  failed: boolean;
  /** Questions answered or skipped so far, the side question included. */
  numbered: number;
  total: number;
  hasAnswers: boolean;
  /** A new match is on its way, so the answers on screen are about to change. */
  busy: boolean;
  onAnswer: (answer: PortfolioAnswer) => void;
  onBack: () => void;
  onRestart: () => void;
}

interface AnswerButton {
  key: string;
  title: string;
  line: string;
  icon?: ReactNode;
  onClick: () => void;
}

/**
 * One line and two answers, every time, in a card that keeps its height, so nothing below it moves
 * as the questions change.
 */
export function QuestionCard({
  hidden,
  onHiddenChange,
  sideAnswered,
  next,
  failed,
  numbered,
  total,
  hasAnswers,
  busy,
  onAnswer,
  onBack,
  onRestart,
}: QuestionCardProps) {
  const { t } = useTranslation(["portfolioBuilder", "tradingStrategies"]);

  if (hidden) {
    return (
      <div className="portfolio-questions is-hidden">
        <span className="portfolio-eyebrow">{t("questions.hidden")}</span>
        <span className="portfolio-questions-hint">{t("questions.hiddenHint")}</span>
        <CButton size="sm" color="primary" variant="outline" onClick={() => onHiddenChange(false)}>
          {t("questions.show")}
        </CButton>
      </div>
    );
  }

  const answer = (id: string, v: PortfolioAnswer["v"]) => () => onAnswer({ id, v });

  let label = t("questions.progress", { number: numbered + 1, total });
  let text = "";
  let skip: (() => void) | null = null;
  let answers: AnswerButton[] = [];

  if (failed) {
    text = t("questions.failed");
  } else if (!sideAnswered) {
    text = t("questions.side.text");
    answers = [
      {
        key: "a",
        title: t("questions.side.longAndShort"),
        line: t("questions.side.longAndShortLine"),
        icon: <DirectionIcon direction={3} label={t("tradingStrategies:traits.direction.3")} size={18} />,
        onClick: answer(SIDE_QUESTION_ID, "a"),
      },
      {
        key: "b",
        title: t("questions.side.longOnly"),
        line: t("questions.side.longOnlyLine"),
        icon: <DirectionIcon direction={1} label={t("tradingStrategies:traits.direction.1")} size={18} />,
        onClick: answer(SIDE_QUESTION_ID, "b"),
      },
    ];
  } else if (next?.kind === "question") {
    const { question } = next;
    text = question.text;
    answers = question.answers.map((option, index) => ({
      key: index === 0 ? "a" : "b",
      title: option.title,
      line: option.line,
      onClick: answer(question.key, index === 0 ? "a" : "b"),
    }));
    skip = next.skippable ? answer(question.key, "skip") : null;
  } else if (next?.kind === "check") {
    label = t("questions.checkIn");
    text = t("questions.check.text");
    answers = [
      { key: "a", title: t("questions.check.yes"), line: t("questions.check.yesLine"), onClick: answer(CHECK_ID, "a") },
      {
        key: "b",
        title: t("questions.check.refine"),
        line: t("questions.check.refineLine"),
        onClick: answer(CHECK_ID, "b"),
      },
    ];
  } else if (next?.kind === "done") {
    label = t("questions.ready");
    text = next.exhausted ? t("questions.done.exhausted") : t("questions.done.text");
    const startAgain: AnswerButton = {
      key: "restart",
      title: t("questions.done.startAgain"),
      line: t("questions.done.startAgainLine"),
      onClick: onRestart,
    };
    answers =
      next.left > 0 && !next.exhausted
        ? [
            {
              key: "refine",
              title: t("questions.done.refine"),
              line: t("questions.done.refineLine", { count: next.left }),
              onClick: answer(REFINE_ID, "a"),
            },
            startAgain,
          ]
        : [startAgain];
  }

  const progress = Math.min(100, Math.round((numbered / Math.max(1, total)) * 100));

  return (
    <div className="portfolio-questions">
      <div className="portfolio-questions-top">
        <span className="portfolio-eyebrow">{label}</span>
        <div className="portfolio-progress" aria-hidden>
          <i style={{ width: `${progress}%` }} />
        </div>
        <div className="portfolio-questions-actions">
          <CButton size="sm" color="primary" variant="outline" disabled={!hasAnswers} onClick={onBack}>
            {t("questions.back")}
          </CButton>
          {skip && (
            <CButton size="sm" color="primary" variant="outline" disabled={busy} onClick={skip}>
              {t("questions.skip")}
            </CButton>
          )}
          <CButton size="sm" color="primary" variant="outline" disabled={!hasAnswers} onClick={onRestart}>
            {t("questions.startAgain")}
          </CButton>
          <CButton size="sm" color="primary" variant="outline" onClick={() => onHiddenChange(true)}>
            {t("questions.hide")}
          </CButton>
        </div>
      </div>
      <h2 className="portfolio-question-text">{text}</h2>
      <div className="portfolio-answers">
        {answers.map((option) => (
          <button
            key={option.key}
            type="button"
            className="portfolio-answer"
            disabled={busy && option.key !== "restart"}
            onClick={option.onClick}
          >
            <span className="portfolio-answer-title">
              {option.icon}
              {option.title}
            </span>
            <span className="portfolio-answer-line">{option.line}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
